'use client';
import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';

// ─── Types ────────────────────────────────────────────────────────────────────
interface UpcomingSession {
  id: string;
  clientName: string;
  clientInitials: string;
  service: string;
  date: string;
  time: string;
  duration: string;
  amount: number;
  status: 'upcoming' | 'pending';
}

interface CompletedBooking {
  id: string;
  clientName: string;
  clientInitials: string;
  service: string;
  date: string;
  gross: number;
  platformFee: number;
  net: number;
  paymentStatus: string;
}

interface PayoutScheduleItem {
  label: string;
  amount: number;
  status: 'completed' | 'processing' | 'scheduled' | 'pending';
  date: string;
  description: string;
}

interface MonthlyEarning {
  month: string;
  gross: number;
  net: number;
  sessions: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();
}

function fmtUSD(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const PAYOUT_STATUS_CONFIG = {
  completed:  { label: 'Paid Out',    bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', icon: 'CheckCircleIcon' },
  processing: { label: 'Processing',  bg: 'bg-blue-50',    text: 'text-blue-700',    dot: 'bg-blue-500',    icon: 'ArrowPathIcon' },
  scheduled:  { label: 'Scheduled',   bg: 'bg-violet-50',  text: 'text-violet-700',  dot: 'bg-violet-500',  icon: 'CalendarDaysIcon' },
  pending:    { label: 'Pending',      bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500',   icon: 'ClockIcon' },
};

// ─── Sub-components ────────────────────────────────────────────────────────────
function KpiCard({
  icon,
  label,
  value,
  sub,
  accentBg,
  accentIcon,
  trend,
}: {
  icon: string;
  label: string;
  value: string;
  sub?: string;
  accentBg: string;
  accentIcon: string;
  trend?: { value: string; positive: boolean };
}) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accentBg}`}>
          <Icon name={icon as Parameters<typeof Icon>[0]['name']} size={20} className={accentIcon} />
        </div>
        {trend && (
          <span className={`text-xs font-600 px-2 py-1 rounded-full ${trend.positive ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
            {trend.positive ? '↑' : '↓'} {trend.value}
          </span>
        )}
      </div>
      <div>
        <p className="text-2xl font-700 text-foreground leading-tight">{value}</p>
        <p className="text-xs text-muted-foreground font-500 mt-0.5">{label}</p>
        {sub && <p className="text-xs text-muted-foreground/70 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function Avatar({ initials }: { initials: string }) {
  return (
    <div className="w-9 h-9 rounded-full gradient-teal flex items-center justify-center text-white font-600 text-xs flex-shrink-0">
      {initials}
    </div>
  );
}

function SectionHeader({ title, sub, action, onAction }: { title: string; sub?: string; action?: string; onAction?: () => void }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div>
        <h2 className="font-700 text-foreground text-base">{title}</h2>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
      {action && onAction && (
        <button onClick={onAction} className="text-xs text-primary font-600 hover:underline">
          {action}
        </button>
      )}
    </div>
  );
}

function EmptyState({ icon, message }: { icon: string; message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground">
      <Icon name={icon as Parameters<typeof Icon>[0]['name']} size={36} className="opacity-25" />
      <p className="text-sm font-500">{message}</p>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="animate-pulse bg-card border border-border rounded-2xl p-5 space-y-3">
      <div className="h-4 bg-muted rounded w-1/3" />
      <div className="h-8 bg-muted rounded w-1/2" />
      <div className="h-3 bg-muted rounded w-1/4" />
    </div>
  );
}

// ─── Custom Tooltip ────────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{ name: string; value: number; color: string }>; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-xl px-3 py-2 shadow-lg text-xs">
      <p className="font-600 text-foreground mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color }} className="font-500">
          {p.name}: {fmtUSD(p.value)}
        </p>
      ))}
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function SessionsDashboardPage() {
  const supabase = createClient();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [upcomingSessions, setUpcomingSessions] = useState<UpcomingSession[]>([]);
  const [completedBookings, setCompletedBookings] = useState<CompletedBooking[]>([]);
  const [payoutSchedule, setPayoutSchedule] = useState<PayoutScheduleItem[]>([]);
  const [monthlyEarnings, setMonthlyEarnings] = useState<MonthlyEarning[]>([]);
  const [analyticsView, setAnalyticsView] = useState<'area' | 'bar'>('area');

  // Summary stats
  const [totalEarned, setTotalEarned] = useState(0);
  const [pendingPayout, setPendingPayout] = useState(0);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [thisMonthNet, setThisMonthNet] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      // ── Wallet ────────────────────────────────────────────────────────────
      const { data: wallet } = await supabase
        .from('consultant_wallets')
        .select('total_earned_usd, balance_usd, pending_usd')
        .eq('consultant_id', user.id)
        .maybeSingle();

      // ── Bookings ──────────────────────────────────────────────────────────
      const { data: bookingData, error: bookingErr } = await supabase
        .from('consultation_bookings')
        .select('id, client_name, service_title, booking_date, start_time, duration_minutes, amount_usd, payment_status, status, notes')
        .eq('provider_user_id', user.id)
        .order('booking_date', { ascending: false });

      if (bookingErr) throw new Error(bookingErr.message);

      const rows = bookingData ?? [];

      // Map upcoming
      const upcoming: UpcomingSession[] = rows
        .filter((r) => r.status === 'confirmed' || r.status === 'pending')
        .map((r) => {
          const startTime = r.start_time ? r.start_time.slice(0, 5) : '';
          const [h] = startTime.split(':').map(Number);
          const period = h < 12 ? 'AM' : 'PM';
          const mins = r.duration_minutes ?? 60;
          return {
            id: r.id,
            clientName: r.client_name ?? 'Unknown Client',
            clientInitials: getInitials(r.client_name ?? 'Unknown Client'),
            service: r.service_title ?? 'Consultation',
            date: r.booking_date ?? '',
            time: startTime ? `${startTime} ${period}` : '',
            duration: `${mins} min`,
            amount: parseFloat(r.amount_usd ?? 0),
            status: r.status === 'confirmed' ? 'upcoming' : 'pending',
          };
        });
      setUpcomingSessions(upcoming);

      // Map completed
      const completed: CompletedBooking[] = rows
        .filter((r) => r.status === 'completed' || r.payment_status === 'paid')
        .map((r) => {
          const gross = parseFloat(r.amount_usd ?? 0);
          const platformFee = parseFloat((gross * 0.1).toFixed(2));
          const net = parseFloat((gross - platformFee).toFixed(2));
          return {
            id: r.id,
            clientName: r.client_name ?? 'Unknown Client',
            clientInitials: getInitials(r.client_name ?? 'Unknown Client'),
            service: r.service_title ?? 'Consultation',
            date: r.booking_date ?? '',
            gross,
            platformFee,
            net,
            paymentStatus: r.payment_status ?? 'unpaid',
          };
        });
      setCompletedBookings(completed);

      // ── Earnings summary ──────────────────────────────────────────────────
      const now = new Date();
      const thisMonth = now.getMonth();
      const thisYear = now.getFullYear();

      const totalNet = wallet?.total_earned_usd
        ? parseFloat(wallet.total_earned_usd)
        : completed.reduce((s, c) => s + c.net, 0);

      const pendingAmt = wallet?.pending_usd
        ? parseFloat(wallet.pending_usd)
        : completed.filter((c) => c.paymentStatus === 'pending').reduce((s, c) => s + c.net, 0);

      const balance = wallet?.balance_usd ? parseFloat(wallet.balance_usd) : 0;

      const thisMonthNetAmt = completed
        .filter((c) => {
          if (!c.date) return false;
          const d = new Date(c.date);
          return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
        })
        .reduce((s, c) => s + c.net, 0);

      setTotalEarned(totalNet);
      setPendingPayout(pendingAmt);
      setAvailableBalance(balance);
      setThisMonthNet(thisMonthNetAmt);

      // ── Monthly analytics ─────────────────────────────────────────────────
      const monthMap: Record<string, { gross: number; net: number; sessions: number }> = {};
      completed.forEach((c) => {
        if (!c.date) return;
        const d = new Date(c.date);
        const key = d.toLocaleString('en-US', { month: 'short', year: '2-digit' });
        if (!monthMap[key]) monthMap[key] = { gross: 0, net: 0, sessions: 0 };
        monthMap[key].gross += c.gross;
        monthMap[key].net += c.net;
        monthMap[key].sessions += 1;
      });

      const sortedMonths = Object.entries(monthMap)
        .sort(([a], [b]) => {
          const da = new Date(`01 ${a}`);
          const db = new Date(`01 ${b}`);
          return da.getTime() - db.getTime();
        })
        .slice(-6)
        .map(([month, v]) => ({ month, ...v }));

      setMonthlyEarnings(sortedMonths);

      // ── Payout schedule ───────────────────────────────────────────────────
      const { data: withdrawals } = await supabase
        .from('consultant_withdrawal_requests')
        .select('id, amount_usd, fee_usd, net_amount_usd, withdrawal_status, requested_at, processed_at')
        .eq('user_id', user.id)
        .order('requested_at', { ascending: false })
        .limit(5);

      const payoutItems: PayoutScheduleItem[] = (withdrawals ?? []).map((w) => {
        const statusMap: Record<string, PayoutScheduleItem['status']> = {
          pending: 'pending',
          processing: 'processing',
          completed: 'completed',
          failed: 'pending',
          cancelled: 'pending',
        };
        const status = statusMap[w.withdrawal_status] ?? 'pending';
        const reqDate = w.requested_at ? new Date(w.requested_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
        const procDate = w.processed_at ? new Date(w.processed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
        return {
          label: `Withdrawal #${w.id.slice(0, 6).toUpperCase()}`,
          amount: parseFloat(w.net_amount_usd ?? w.amount_usd ?? 0),
          status,
          date: status === 'completed' ? procDate : reqDate,
          description: status === 'completed' ? `Paid on ${procDate}` : status === 'processing' ? 'Expected in 1–2 business days' : `Requested ${reqDate}`,
        };
      });

      // If no withdrawals, show a placeholder schedule based on pending earnings
      if (payoutItems.length === 0 && pendingAmt > 0) {
        payoutItems.push({
          label: 'Pending Session Earnings',
          amount: pendingAmt,
          status: 'scheduled',
          date: 'T+2 business days',
          description: 'Processed after session completion verification',
        });
      }

      setPayoutSchedule(payoutItems);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <AppLayout activeRoute="/sessions-dashboard">
      <div className="min-h-screen bg-background">
        {/* ── Page Header ── */}
        <div className="bg-card border-b border-border px-6 py-5">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-xl font-700 text-foreground">Sessions Dashboard</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Upcoming sessions, completed bookings, revenue breakdown &amp; payout status</p>
            </div>
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl text-sm font-600 hover:bg-primary/90 transition-colors"
            >
              <Icon name="ArrowPathIcon" size={15} />
              Refresh
            </button>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
          {/* ── Loading ── */}
          {loading && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => <SkeletonCard key={i} />)}
            </div>
          )}

          {/* ── Error ── */}
          {!loading && error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
              <Icon name="ExclamationCircleIcon" size={28} className="text-red-500 mx-auto mb-2" />
              <p className="text-sm font-600 text-red-700">{error}</p>
              <button onClick={fetchData} className="mt-3 text-xs text-red-600 underline font-500">Try again</button>
            </div>
          )}

          {!loading && !error && (
            <>
              {/* ── KPI Cards ── */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard
                  icon="BanknotesIcon"
                  label="Total Earned (Net)"
                  value={fmtUSD(totalEarned)}
                  sub="All time after platform fee"
                  accentBg="bg-emerald-100"
                  accentIcon="text-emerald-700"
                />
                <KpiCard
                  icon="CalendarDaysIcon"
                  label="Upcoming Sessions"
                  value={String(upcomingSessions.length)}
                  sub="Confirmed & pending"
                  accentBg="bg-blue-100"
                  accentIcon="text-blue-700"
                />
                <KpiCard
                  icon="CheckCircleIcon"
                  label="Completed Bookings"
                  value={String(completedBookings.length)}
                  sub="Paid sessions"
                  accentBg="bg-violet-100"
                  accentIcon="text-violet-700"
                />
                <KpiCard
                  icon="ClockIcon"
                  label="Pending Payout"
                  value={fmtUSD(pendingPayout)}
                  sub="Awaiting processing"
                  accentBg="bg-amber-100"
                  accentIcon="text-amber-700"
                />
              </div>

              {/* ── Main Grid ── */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                {/* ── Upcoming Sessions ── */}
                <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-5">
                  <SectionHeader
                    title="Upcoming Sessions"
                    sub={`${upcomingSessions.length} session${upcomingSessions.length !== 1 ? 's' : ''} scheduled`}
                  />
                  {upcomingSessions.length === 0 ? (
                    <EmptyState icon="CalendarDaysIcon" message="No upcoming sessions" />
                  ) : (
                    <div className="space-y-3">
                      {upcomingSessions.slice(0, 5).map((s) => (
                        <div key={s.id} className="flex items-center gap-3 p-3 bg-muted/40 rounded-xl hover:bg-muted/60 transition-colors">
                          <Avatar initials={s.clientInitials} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-600 text-foreground truncate">{s.clientName}</p>
                            <p className="text-xs text-muted-foreground truncate">{s.service}</p>
                          </div>
                          <div className="text-right flex-shrink-0 hidden sm:block">
                            <p className="text-xs font-600 text-foreground">{s.date}</p>
                            <p className="text-xs text-muted-foreground">{s.time} · {s.duration}</p>
                          </div>
                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            <span className="text-sm font-700 text-foreground">{fmtUSD(s.amount)}</span>
                            <span className={`text-xs font-500 px-2 py-0.5 rounded-full ${s.status === 'upcoming' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`}>
                              {s.status === 'upcoming' ? 'Confirmed' : 'Pending'}
                            </span>
                          </div>
                        </div>
                      ))}
                      {upcomingSessions.length > 5 && (
                        <p className="text-xs text-muted-foreground text-center pt-1">+{upcomingSessions.length - 5} more sessions</p>
                      )}
                    </div>
                  )}
                </div>

                {/* ── Revenue Breakdown ── */}
                <div className="bg-card border border-border rounded-2xl p-5">
                  <SectionHeader title="Revenue Breakdown" sub="Current balance & splits" />
                  <div className="space-y-3">
                    <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl">
                      <p className="text-xs text-emerald-600 font-500 mb-1">Available Balance</p>
                      <p className="text-2xl font-700 text-emerald-700">{fmtUSD(availableBalance)}</p>
                      <p className="text-xs text-emerald-600/70 mt-0.5">Ready to withdraw</p>
                    </div>
                    <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl">
                      <p className="text-xs text-amber-600 font-500 mb-1">Pending Payout</p>
                      <p className="text-xl font-700 text-amber-700">{fmtUSD(pendingPayout)}</p>
                      <p className="text-xs text-amber-600/70 mt-0.5">Processing T+2 days</p>
                    </div>
                    <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl">
                      <p className="text-xs text-blue-600 font-500 mb-1">This Month (Net)</p>
                      <p className="text-xl font-700 text-blue-700">{fmtUSD(thisMonthNet)}</p>
                      <p className="text-xs text-blue-600/70 mt-0.5">After 10% platform fee</p>
                    </div>
                    <div className="mt-2 px-3 py-2.5 bg-muted/50 rounded-xl">
                      <p className="text-xs text-muted-foreground font-500 mb-1.5">Fee Structure</p>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Your share</span>
                        <span className="font-600 text-foreground">90%</span>
                      </div>
                      <div className="flex justify-between text-xs mt-1">
                        <span className="text-muted-foreground">Platform fee</span>
                        <span className="font-600 text-foreground">10%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Earnings Analytics ── */}
              <div className="bg-card border border-border rounded-2xl p-5">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h2 className="font-700 text-foreground text-base">Earnings Analytics</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">Monthly gross vs net revenue (last 6 months)</p>
                  </div>
                  <div className="flex items-center gap-1 bg-muted/50 border border-border rounded-xl p-1">
                    <button
                      onClick={() => setAnalyticsView('area')}
                      className={`px-3 py-1.5 rounded-lg text-xs font-600 transition-colors ${analyticsView === 'area' ? 'bg-card text-foreground shadow-sm border border-border' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      Area
                    </button>
                    <button
                      onClick={() => setAnalyticsView('bar')}
                      className={`px-3 py-1.5 rounded-lg text-xs font-600 transition-colors ${analyticsView === 'bar' ? 'bg-card text-foreground shadow-sm border border-border' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      Bar
                    </button>
                  </div>
                </div>

                {monthlyEarnings.length === 0 ? (
                  <EmptyState icon="ChartBarIcon" message="No earnings data yet — complete sessions to see analytics" />
                ) : (
                  <>
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        {analyticsView === 'area' ? (
                          <AreaChart data={monthlyEarnings} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                            <defs>
                              <linearGradient id="grossGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.18} />
                                <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0} />
                              </linearGradient>
                              <linearGradient id="netGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.18} />
                                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                            <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                            <Tooltip content={<ChartTooltip />} />
                            <Area type="monotone" dataKey="gross" name="Gross" stroke="var(--color-primary)" strokeWidth={2} fill="url(#grossGrad)" />
                            <Area type="monotone" dataKey="net" name="Net" stroke="#10b981" strokeWidth={2} fill="url(#netGrad)" />
                          </AreaChart>
                        ) : (
                          <BarChart data={monthlyEarnings} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                            <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                            <Tooltip content={<ChartTooltip />} />
                            <Bar dataKey="gross" name="Gross" fill="var(--color-primary)" radius={[4, 4, 0, 0]} opacity={0.7} />
                            <Bar dataKey="net" name="Net" fill="#10b981" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        )}
                      </ResponsiveContainer>
                    </div>
                    <div className="flex items-center gap-5 mt-3 justify-center">
                      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span className="w-3 h-1.5 rounded-full bg-primary inline-block" />
                        Gross Revenue
                      </span>
                      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span className="w-3 h-1.5 rounded-full bg-emerald-500 inline-block" />
                        Net Earnings
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* ── Bottom Grid: Completed Bookings + Payout Schedule ── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* Completed Bookings */}
                <div className="bg-card border border-border rounded-2xl p-5">
                  <SectionHeader
                    title="Completed Bookings"
                    sub={`${completedBookings.length} paid session${completedBookings.length !== 1 ? 's' : ''}`}
                  />
                  {completedBookings.length === 0 ? (
                    <EmptyState icon="CheckCircleIcon" message="No completed bookings yet" />
                  ) : (
                    <div className="space-y-0 divide-y divide-border">
                      {completedBookings.slice(0, 6).map((b) => (
                        <div key={b.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                          <Avatar initials={b.clientInitials} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-600 text-foreground truncate">{b.clientName}</p>
                            <p className="text-xs text-muted-foreground truncate">{b.service} · {b.date}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-sm font-700 text-emerald-700">{fmtUSD(b.net)}</p>
                            <p className="text-xs text-muted-foreground line-through">{fmtUSD(b.gross)}</p>
                          </div>
                        </div>
                      ))}
                      {completedBookings.length > 6 && (
                        <p className="text-xs text-muted-foreground text-center pt-3">+{completedBookings.length - 6} more bookings</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Payout Schedule */}
                <div className="bg-card border border-border rounded-2xl p-5">
                  <SectionHeader
                    title="Payout Schedule"
                    sub="Withdrawal history & upcoming payouts"
                  />
                  {payoutSchedule.length === 0 ? (
                    <EmptyState icon="BanknotesIcon" message="No payout history yet" />
                  ) : (
                    <div className="space-y-3">
                      {payoutSchedule.map((item, idx) => {
                        const cfg = PAYOUT_STATUS_CONFIG[item.status];
                        return (
                          <div key={idx} className={`flex items-start gap-3 p-3 rounded-xl border ${cfg.bg} border-transparent`}>
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
                              <Icon name={cfg.icon as Parameters<typeof Icon>[0]['name']} size={16} className={cfg.text} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-600 ${cfg.text} truncate`}>{item.label}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className={`text-sm font-700 ${cfg.text}`}>{fmtUSD(item.amount)}</p>
                              <span className={`text-xs font-500 px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text} border border-current/20`}>
                                {cfg.label}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Payout info note */}
                  <div className="mt-4 flex items-start gap-2 px-3 py-2.5 bg-muted/50 rounded-xl">
                    <Icon name="InformationCircleIcon" size={14} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Payouts are processed within <strong className="text-foreground">2–3 business days</strong> after session completion. Platform fee of <strong className="text-foreground">10%</strong> is deducted before payout.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
